import React from "react";

import { Accordion } from "components/ui/Accordion";
import { DataLoadError } from "components/ui/DataLoadError";
import { useMedicalHistory } from "features/patients/hooks/useMedicalHistory";
import { usePatientMedicationHistory } from "features/pregnancy/hooks/usePatientMedicationHistory";
import {
  toCurrentMedicationEntries,
  toMedicalHistoryEntries,
  toSectionBadge,
} from "features/pregnancy/utils/patientFileReference";

import s from "./PatientFileReference.module.css";
import { ReferenceList } from "./ReferenceList";
import { ReferenceSectionTitle } from "./ReferenceSectionTitle";

/*
 * Past medical history and current medications beside the Add-pregnancy form
 * (KIN-2058). Both are relevant at a first pregnancy visit and both were a tab
 * away, which made it easy to miss a drug that matters in pregnancy.
 *
 * Strictly read-only: it displays what the chart already holds, never writes to
 * the patient record, and does not replace the patient's own history screens.
 *
 * usePatientMedicationHistory is the pregnancy module's existing reference for
 * medications (KIN-1085) and step 2 of this same flow already loads it, so
 * showing it here adds no query the flow would not have made anyway.
 *
 * Collapsed by default, because the form below is the task and this is only the
 * context for it. What survives collapsing is the part that decides whether a
 * GP needs to look: each heading carries its own count, so a shut section says
 * "2" or "None" rather than hiding the difference between them.
 *
 * A failed load is NOT collapsible. It is lifted out of the accordion and shown
 * in place of its section, for the same reason DataLoadError replaces an empty
 * state rather than sitting beside one: "we could not load this" must never be
 * read as "there is nothing", and a warning behind a shut header would be. The
 * open/closed choice is deliberately not remembered either — the next patient
 * is a different question, so every patient starts from the same default.
 */
export const PatientFileReference = ({ patientId }) => {
  const {
    data: medicalHistory,
    isLoading: isMedicalHistoryLoading,
    isError: isMedicalHistoryError,
    isFetching: isMedicalHistoryFetching,
    refetch: refetchMedicalHistory,
  } = useMedicalHistory(patientId);

  const {
    data: medicationHistory,
    isLoading: isMedicationsLoading,
    isError: isMedicationsError,
    isFetching: isMedicationsFetching,
    refetch: refetchMedications,
  } = usePatientMedicationHistory(patientId);

  const sections = [
    {
      id: "past-medical-history",
      title: "Past medical history",
      entries: toMedicalHistoryEntries(medicalHistory),
      isLoading: isMedicalHistoryLoading,
      isError: isMedicalHistoryError,
      errorLabel: "this patient's medical history",
      emptyText: "No medical history recorded.",
      onRetry: refetchMedicalHistory,
      isRetrying: isMedicalHistoryFetching,
    },
    {
      id: "current-medications",
      title: "Current medications",
      entries: toCurrentMedicationEntries(medicationHistory),
      isLoading: isMedicationsLoading,
      isError: isMedicationsError,
      errorLabel: "this patient's medications",
      emptyText: "No current medications recorded.",
      onRetry: refetchMedications,
      isRetrying: isMedicationsFetching,
    },
  ];

  const failedSections = sections.filter((section) => section.isError);
  const readableSections = sections.filter((section) => !section.isError);

  return (
    <section className={s.panel} data-testid="patient-file-reference">
      {failedSections.map((section) => (
        <DataLoadError
          key={section.id}
          label={section.errorLabel}
          onRetry={section.onRetry}
          isRetrying={section.isRetrying}
        />
      ))}

      {!!readableSections.length && (
        <Accordion
          className={s.accordion}
          items={readableSections.map((section) => ({
            id: section.id,
            title: (
              <ReferenceSectionTitle
                title={section.title}
                badge={toSectionBadge(section)}
              />
            ),
            content: (
              <ReferenceList
                entries={section.entries}
                isLoading={section.isLoading}
                emptyText={section.emptyText}
              />
            ),
          }))}
        />
      )}
    </section>
  );
};
